/**
 * Discovery API Service
 * Handles profile discovery, swiping, and matching API calls.
 */

import { apiService } from './apiService';
import type { SharedMemory } from '../../types/memory';

// ==========================================
// TYPES
// ==========================================

export interface ProfilePhoto {
  id: string;
  url: string;
  thumbnailUrl?: string;
  isPrimary: boolean;
  isVerified: boolean;
}

export interface UserPrompt {
  question: string;
  answer: string;
}

export interface DiscoveryProfileCard {
  profileId: string;
  displayName: string;
  age: number;
  gender?: string;
  bio?: string;
  locationCity?: string;
  distanceKm?: number;
  photos: ProfilePhoto[];
  prompts: UserPrompt[];
  interests: string[];
  relationshipType?: string;
  gamingFrequency?: string;
  gamingPlatforms: string[];
  isVerified: boolean;
  lastActiveAt?: string;
  // ── Character-guided profiling (WU-B3 / §6.1): additive, optional; omitted when unknown ──
  compatibilityScore?: number;      // 0..1, rounded to 2 dp; undefined/omitted when unknown (no shared dims)
  sharedMemories?: SharedMemory[];  // ONLY mutually-confirmed overlaps; max 3; never raw memory text
  icebreaker?: string;              // NPC-voiced line built from the top shared memory; omitted when none
}

export interface SwipeResult {
  success: boolean;
  error?: string;
  isMatch: boolean;
  matchId?: string;
  matchedProfile?: DiscoveryProfileCard;
}

export interface MatchSummary {
  matchId: string;
  profileId: string;
  displayName: string;
  primaryPhotoUrl?: string;
  matchedAt: string;
  hasConversation: boolean;
  lastMessageAt?: string;
  isNew: boolean;
  isSuperLikeMatch: boolean;
}

export interface DiscoveryPreferences {
  id?: string;
  userProfileId?: string;
  minAge: number;
  maxAge: number;
  maxDistanceKm: number;
  strictDistanceFilter: boolean;
  showMale: boolean;
  showFemale: boolean;
  showNonBinary: boolean;
  showOther: boolean;
  requireVerifiedPhotos: boolean;
  minPhotos: number;
  requireBio: boolean;
  activeWithinDays?: number;
  relationshipTypeFilter?: string;
  gamingFrequencyFilter?: string;
  gamingPlatformFilter?: string;
  isDiscoverable: boolean;
  showGlobally: boolean;
  hideDistance: boolean;
  hideAge: boolean;
}

export type SwipeAction = 'like' | 'pass' | 'superlike';

export interface NudgeResult {
  success: boolean;
  reason?: string;
}

export interface NudgeFromProfile {
  profileId: string;
  displayName: string;
  age: number;
  primaryPhotoUrl?: string;
}

export interface ReceivedNudge {
  id: string;
  type: number;
  createdAt: string;
  seenAt?: string | null;
  fromProfile: NudgeFromProfile;
}

// ==========================================
// DISCOVERY FEED
// ==========================================

/**
 * Get profiles for the discovery feed
 */
export async function getDiscoveryFeed(count: number = 10): Promise<DiscoveryProfileCard[]> {
  const response = await apiService.get<DiscoveryProfileCard[]>(`/api/discovery/feed?count=${count}`);
  if (response.error) {
    console.error('Failed to get discovery feed:', response.error);
    return [];
  }
  return response.data || [];
}

/**
 * Get discovery preferences
 */
export async function getPreferences(): Promise<DiscoveryPreferences | null> {
  const response = await apiService.get<DiscoveryPreferences>('/api/discovery/preferences');
  if (response.error) {
    console.error('Failed to get preferences:', response.error);
    return null;
  }
  return response.data || null;
}

/**
 * Update discovery preferences
 */
export async function updatePreferences(preferences: Partial<DiscoveryPreferences>): Promise<DiscoveryPreferences | null> {
  const response = await apiService.put<DiscoveryPreferences>('/api/discovery/preferences', preferences);
  if (response.error) {
    console.error('Failed to update preferences:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

// ==========================================
// SWIPING
// ==========================================

/**
 * Swipe on a profile (like, pass, or superlike)
 */
export async function swipe(profileId: string, action: SwipeAction): Promise<SwipeResult> {
  const response = await apiService.post<SwipeResult>('/api/discovery/swipe', {
    profileId,
    action,
  });
  if (response.error) {
    console.error('Failed to swipe:', response.error);
    return { success: false, error: response.error, isMatch: false };
  }
  return response.data || { success: false, error: 'Unknown error', isMatch: false };
}

/**
 * Like a profile
 */
export async function like(profileId: string): Promise<SwipeResult> {
  return swipe(profileId, 'like');
}

/**
 * Pass on a profile
 */
export async function pass(profileId: string): Promise<SwipeResult> {
  return swipe(profileId, 'pass');
}

/**
 * Super like a profile
 */
export async function superLike(profileId: string): Promise<SwipeResult> {
  return swipe(profileId, 'superlike');
}

/**
 * Undo the last swipe (within 5 seconds)
 */
export async function undoLastSwipe(): Promise<boolean> {
  const response = await apiService.post<{ success: boolean }>('/api/discovery/undo', {});
  if (response.error) {
    console.error('Failed to undo swipe:', response.error);
    return false;
  }
  return response.data?.success ?? false;
}

// ==========================================
// MATCHES
// ==========================================

/**
 * Get matches list
 */
export async function getMatches(page: number = 1, pageSize: number = 20): Promise<MatchSummary[]> {
  const response = await apiService.get<MatchSummary[]>(`/api/discovery/matches?page=${page}&pageSize=${pageSize}`);
  if (response.error) {
    console.error('Failed to get matches:', response.error);
    return [];
  }
  return response.data || [];
}

/**
 * Get count of unseen matches
 */
export async function getUnseenMatchCount(): Promise<number> {
  const response = await apiService.get<{ count: number }>('/api/discovery/matches/unseen-count');
  if (response.error) {
    console.error('Failed to get unseen match count:', response.error);
    return 0;
  }
  return response.data?.count ?? 0;
}

/**
 * Mark a match as seen
 */
export async function markMatchAsSeen(matchId: string): Promise<boolean> {
  const response = await apiService.post<{ success: boolean }>(`/api/discovery/matches/${matchId}/seen`, {});
  if (response.error) {
    console.error('Failed to mark match as seen:', response.error);
    return false;
  }
  return response.data?.success ?? false;
}

/**
 * Unmatch with a user
 */
export async function unmatch(matchId: string): Promise<boolean> {
  const response = await apiService.post<{ success: boolean }>(`/api/discovery/matches/${matchId}/unmatch`, {});
  if (response.error) {
    console.error('Failed to unmatch:', response.error);
    return false;
  }
  return response.data?.success ?? false;
}

/**
 * Block a user
 */
export async function blockUser(matchId: string): Promise<boolean> {
  const response = await apiService.post<{ success: boolean }>(`/api/discovery/matches/${matchId}/block`, {});
  if (response.error) {
    console.error('Failed to block user:', response.error);
    return false;
  }
  return response.data?.success ?? false;
}

// ==========================================
// LIKES (Premium Feature)
// ==========================================

/**
 * Get count of likes received
 */
export async function getLikesCount(): Promise<number> {
  const response = await apiService.get<{ count: number }>('/api/discovery/likes/count');
  if (response.error) {
    console.error('Failed to get likes count:', response.error);
    return 0;
  }
  return response.data?.count ?? 0;
}

/**
 * Get profiles that liked you (premium feature)
 */
export async function getLikesReceived(page: number = 1, pageSize: number = 20): Promise<DiscoveryProfileCard[]> {
  const response = await apiService.get<DiscoveryProfileCard[]>(`/api/discovery/likes?page=${page}&pageSize=${pageSize}`);
  if (response.error) {
    console.error('Failed to get likes received:', response.error);
    return [];
  }
  return response.data || [];
}

// ==========================================
// NUDGES (Winks)
// ==========================================

/**
 * Send a nudge (wink) to a profile without swiping
 */
export async function sendNudge(targetProfileId: string, type: number = 0): Promise<NudgeResult> {
  const response = await apiService.post<NudgeResult>('/api/discovery/nudge', {
    targetProfileId,
    type,
  });
  if (response.error) {
    console.error('Failed to send nudge:', response.error);
    return { success: false, reason: response.error };
  }
  return response.data || { success: false, reason: 'Unknown error' };
}

/**
 * Get nudges received
 */
export async function getNudges(): Promise<ReceivedNudge[]> {
  const response = await apiService.get<ReceivedNudge[]>('/api/discovery/nudges');
  if (response.error) {
    console.error('Failed to get nudges:', response.error);
    return [];
  }
  return response.data || [];
}

/**
 * Get count of unseen nudges
 */
export async function getUnseenNudgeCount(): Promise<number> {
  const response = await apiService.get<{ count: number }>('/api/discovery/nudges/unseen-count');
  if (response.error) {
    console.error('Failed to get unseen nudge count:', response.error);
    return 0;
  }
  return response.data?.count ?? 0;
}

/**
 * Mark all received nudges as seen
 */
export async function markNudgesSeen(): Promise<boolean> {
  const response = await apiService.post('/api/discovery/nudges/seen', {});
  if (response.error) {
    console.error('Failed to mark nudges as seen:', response.error);
    return false;
  }
  return true;
}

// ==========================================
// EXPORT ALL
// ==========================================

export const discoveryService = {
  // Feed
  getDiscoveryFeed,
  getPreferences,
  updatePreferences,

  // Swiping
  swipe,
  like,
  pass,
  superLike,
  undoLastSwipe,

  // Matches
  getMatches,
  getUnseenMatchCount,
  markMatchAsSeen,
  unmatch,
  blockUser,

  // Likes (Premium)
  getLikesCount,
  getLikesReceived,

  // Nudges (Winks)
  sendNudge,
  getNudges,
  getUnseenNudgeCount,
  markNudgesSeen,
};
